// The price half of one quote line: the supplier's standing price from
// supplier_prices, the negotiated figure, the rung both are agreed against,
// and whether saving also writes the price back.
//
// Quoting always STARTS from the supplier's generic price — the same rule the
// backend applies when it auto-quotes from a deal — so this component looks
// that price up (family price matrix, keyed by master product × supplier) and
// prefills it along with its stored rung. The operator edits freely from
// there; the "Save to Price tool" checkbox decides what the edit means:
//   unticked — the quote only (default; an inquiry price is one-off)
//   ticked   — the quote AND supplier_prices, price and rung together
//              (the BE upserts the cell in the quote's own transaction)
//
// Both quote paths render through here: an existing catalog product keeps the
// full Public → Deal → Negotiated ladder, a staged variant (no product row
// yet) gets the compact price + rung pair. The lookup is per line and lazy —
// nothing is fetched until a supplier is picked and this line is in play.

import type { QuotationSupplierProduct } from 'apis/restaurant-inquiries';
import { useGetFamilyPriceMatrixApi } from 'apis/supplier-prices';
import { Box, ChakraSelect, Checkbox, Flex, Input, Text } from 'design-system';
import { PriceLadder } from 'pages/inquiry-processor/components/quote-matcher/price-ladder';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import { availableTiers, tierUnit, type PriceTier, type UomLadder } from 'pages/inquiry-processor/utils/price-units';
import { isPriced } from 'pages/inquiry-processor/utils/quote-slots';
import { fmt } from 'pages/inquiry-processor/utils/scoring';
import { findSupplierPrice } from 'pages/inquiry-processor/utils/supplier-price';
import { useEffect, useRef, type ReactNode } from 'react';

interface QuoteLinePricingProps {
  supplierId: number | undefined;
  familyId?: number | null; // the master product's family — scopes the price matrix
  masterProductId?: number | null; // the variant being quoted (staged sibling, item master, or a linked product's)
  ladder: UomLadder; // rungs this line's price can be agreed against
  product?: QuotationSupplierProduct | null; // present → the full ladder UI
  price: string;
  priceUnit: PriceTier | null;
  sync: boolean; // tick = also save to supplier_prices
  targetPrice?: number | null;
  onPriceChange: (price: string) => void;
  onPriceUnitChange: (tier: PriceTier) => void;
  onSyncChange: (sync: boolean) => void;
  // Fired once when the supplier's standing price resolves. The parent applies
  // it only if the operator hasn't touched the field — a late-arriving fetch
  // must never overwrite typing.
  onSupplierPrice: (price: string, tier?: PriceTier) => void;
  extra?: ReactNode; // e.g. the quote-history clock button
  addDeal?: ReactNode; // inline deal creation, when the product has no deal
}

export const QuoteLinePricing = ({
  supplierId,
  familyId,
  masterProductId,
  ladder,
  product,
  price,
  priceUnit,
  sync,
  targetPrice,
  onPriceChange,
  onPriceUnitChange,
  onSyncChange,
  onSupplierPrice,
  extra,
  addDeal,
}: QuoteLinePricingProps) => {
  const { data } = useGetFamilyPriceMatrixApi(familyId ?? undefined, {
    enabled: !!familyId && !!supplierId && masterProductId != null,
  });
  const matched = findSupplierPrice(data?.data?.deals, masterProductId ?? undefined, supplierId);
  // A row whose price is 0 arrives as null (the model's getter maps a falsy
  // stored price to null) — there is no standing price to quote from, so it
  // must neither prefill nor claim provenance.
  const supplierPrice = isPriced(matched?.dealPrice) ? matched : undefined;

  // One prefill per (line, supplier, price row): re-running on every render
  // would fight the operator, and re-running on a refetch would silently
  // revert an edit.
  const prefilledFor = useRef<string | null>(null);
  const key = supplierPrice ? `${masterProductId}:${supplierId}:${supplierPrice.id}` : null;
  // Effects can't depend on a callback the parent rebuilds each render.
  const notify = useRef(onSupplierPrice);
  notify.current = onSupplierPrice;
  useEffect(() => {
    if (!key || !supplierPrice || prefilledFor.current === key) return;
    prefilledFor.current = key;
    notify.current(String(supplierPrice.dealPrice), supplierPrice.dealUnit ?? undefined);
  }, [key, supplierPrice]);

  const tiers = availableTiers(ladder);
  const unitOptions = tiers.map(({ tier, unit }) => ({ value: tier, label: unit }));
  const selectedUnit = unitOptions.find((option) => option.value === priceUnit) ?? null;
  const supplierPriceUnit = supplierPrice?.dealUnit ? tierUnit(ladder, supplierPrice.dealUnit) : null;
  const modifiedAt = supplierPrice?.lastModifiedAt ? new Date(supplierPrice.lastModifiedAt).getTime() : 0;

  return (
    <Box>
      {/* Provenance: the number in the field is the supplier's, not invented. */}
      {supplierPrice && (
        <Flex align='center' gap='1.5' mb='1.5' fontSize='xs' wrap='wrap'>
          <Text color='gray.500'>Supplier price</Text>
          <Text fontWeight='semibold' color={supplierPrice.isActive ? 'green.600' : 'red.600'}>
            {supplierPrice.dealPrice} AED{supplierPriceUnit ? ` / ${supplierPriceUnit}` : ''}
          </Text>
          {!supplierPrice.isActive && (
            <Text color='red.600' fontWeight='semibold'>
              · inactive
            </Text>
          )}
          {modifiedAt > 0 && <Text color='gray.400'>· updated {fmt(modifiedAt)}</Text>}
        </Flex>
      )}

      {product ? (
        <PriceLadder
          product={product}
          price={price}
          onPriceChange={onPriceChange}
          priceUnit={priceUnit}
          onPriceUnitChange={onPriceUnitChange}
          targetPrice={targetPrice}
          extra={extra}
          addDeal={addDeal}
        />
      ) : (
        <Flex align='flex-end' gap='2' wrap='wrap'>
          <Box maxW='200px'>
            <Text fontSize='10px' color='gray.500' textTransform='uppercase' letterSpacing='wide'>
              Negotiated{targetPrice != null ? ` · target ${targetPrice} AED` : ''}
            </Text>
            <Input
              size='sm'
              value={price}
              onChange={(e) => onPriceChange(numOnly(e.target.value))}
              placeholder='Negotiated price'
              borderBottomColor='red.500'
              _hover={{ borderBottomColor: 'red.500' }}
            />
          </Box>
          {/* Hidden when the master defines no rung words — there would be no
              honest unit to offer, and the BE then derives one. */}
          {unitOptions.length > 0 && (
            <Box w='150px'>
              <Text fontSize='10px' color='gray.500' textTransform='uppercase' letterSpacing='wide'>
                Priced per
              </Text>
              <ChakraSelect
                options={unitOptions}
                value={selectedUnit}
                onChange={(option: any) => option?.value && onPriceUnitChange(option.value as PriceTier)}
                placeholder='per…'
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition='fixed'
                styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
              />
            </Box>
          )}
          {extra}
        </Flex>
      )}

      {/* Only offered when the price could actually be written back: the
          upsert is keyed by (supplier, master product), so an unlinked
          catalog product has nothing to write to. */}
      {masterProductId != null && (
        <Box mt='2'>
          <Checkbox size='sm' isChecked={sync} onChange={(e) => onSyncChange(e.target.checked)}>
            <Text as='span' fontSize='12px' fontWeight='600'>
              Save to Price tool
            </Text>
          </Checkbox>
          <Text fontSize='11px' color='gray.500' pl='6'>
            also becomes the supplier&apos;s standing price for this product
          </Text>
        </Box>
      )}
    </Box>
  );
};
